import React, { useState } from 'react';
import {
  Box,
  Flex,
  Divider,
  FormControl,
  Input,
  Text,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  FormLabel,
  useClipboard,
  useToast,
  Heading,
  FormErrorMessage,
  Button,
  Spacer,
} from '@chakra-ui/core';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import Joi from 'joi';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import {
  clearErrorMessage,
  errorFormat,
  FormattedError,
  getErrorMessage,
  hasErrorMessage,
  updateObject,
} from '../../../../utils/FormErrorUtils';
import { useStoreActions } from '../../../../models/hooks';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
}

type BusinessParams = {
  referrer: string;
  source: string;
  campaign: string;
  medium: string;
};

const BusinessShareModal = ({ isOpen, onClose, businessId }: ShareModalProps): JSX.Element => {
  const { t, i18n } = useTranslation('businessManagement');
  const toast = useToast();

  const [initialState, setInitialState] = useState<BusinessParams>({
    referrer: '',
    source: '',
    campaign: '',
    medium: '',
  });
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [businessDeepLink, setBusinessDeepLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expandMore, setExpandMore] = useState<boolean>(false);
  const { onCopy } = useClipboard(businessDeepLink);

  const { getBusinessDeepLink } = useStoreActions((actions) => actions.businessManagement);

  const schema = Joi.object()
    .options({
      abortEarly: false,
    })
    .keys({
      campaign: Joi.string()
        .trim()
        .allow('')
        .optional()
        .max(100)
        .messages({
          'string.max': t('businessCopy.campaignMaxLength'),
        }),
      source: Joi.string()
        .trim()
        .allow('')
        .optional()
        .max(100)
        .messages({
          'string.max': t('businessCopy.sourceMaxLength'),
        }),
      medium: Joi.string()
        .trim()
        .allow('')
        .optional()
        .max(100)
        .messages({
          'string.max': t('businessCopy.mediumMaxLength'),
        }),
      referrer: Joi.string()
        .trim()
        .allow('')
        .optional()
        .max(50)
        .messages({
          'string.max': t('businessCopy.referrerMaxLength'),
        }),
    });

  const formValidation = (formData: any, scheme: Joi.ObjectSchema) => {
    const { error } = scheme.validate(formData);
    // istanbul ignore next
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }
    setFormErrors([]);
    return true;
  };

  React.useEffect(() => {
    if (!isEmpty(businessDeepLink)) {
      onCopy();
      toast({
        title: t('businessList.copiedMessage'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDeepLink]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const trimmedValue = event.target.value.trimStart();
    updateObject(initialState, event.target.id, trimmedValue, false);
    setFormErrors(clearErrorMessage(formErrors, event.target.id));
  };

  const handleCancel = () => {
    setInitialState({
      referrer: '',
      source: '',
      campaign: '',
      medium: '',
    });
    setFormErrors([]);
    onClose();
  };

  const handleClick = () => {
    setExpandMore(!expandMore);
  };

  // to copy the business link
  const handleBusinessLinkCopy = async () => {
    const formData = { ...initialState };
    const validate = formValidation(initialState, schema);

    setIsLoading(true);

    // istanbul ignore next
    if (!validate) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await getBusinessDeepLink({
        businessId,
        referrerId: isEmpty(formData.referrer) ? undefined : formData.referrer,
        source: isEmpty(formData.source) ? undefined : formData.source,
        campaign: isEmpty(formData.campaign) ? undefined : formData.campaign,
        medium: isEmpty(formData.medium) ? undefined : formData.medium,
        locale: i18n.language,
      });
      setBusinessDeepLink(response?.link);

      await navigator.clipboard.writeText(response?.link);
      handleCancel();
    } catch (err) {
      console.error('Error while generating link', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent minW="640px" pt={3}>
        <ModalBody>
          <Flex direction="column">
            <Heading as="h5" size="sm">
              {t('businessCopy.createLink')}
            </Heading>
            <Text fontSize="14px">{t('businessCopy.additionalParameters')}</Text>
            <Heading as="h6" size="xs" mt={4}>
              {t('businessCopy.businessLink')}
            </Heading>
            <Text fontSize="14px">{t('businessCopy.generatedLink')}</Text>
            <Divider mt={3} />

            <Flex data-testid="tracking-info" alignItems="center" style={{ cursor: 'pointer' }} onClick={handleClick}>
              <Heading as="h6" size="sm" mt={3}>
                {t('businessCopy.additionalParams')}
              </Heading>
              <Spacer />
              {expandMore ? <ChevronUpIcon mt={3} boxSize={6} /> : <ChevronDownIcon mt={3} boxSize={6} />}
            </Flex>

            {expandMore && (
              <>
                <Flex>
                  <Box width="50%" mr={6}>
                    <FormControl isInvalid={hasErrorMessage(formErrors, 'referrer')}>
                      <FormLabel htmlFor="name">
                        <Heading as="h6" size="xs" mt={3}>
                          {t('businessCopy.referrerId')}
                        </Heading>
                      </FormLabel>
                      <Input
                        placeholder={t('businessCopy.enterReferrer')}
                        _placeholder={{ fontSize: '14px' }}
                        data-testid="referrerField"
                        variant="filled"
                        id="referrer"
                        type="text"
                        value={initialState.referrer}
                        onChange={handleChange}
                      />
                      <FormErrorMessage>{getErrorMessage(formErrors, 'referrer')}</FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box width="50%">
                    <FormControl isInvalid={hasErrorMessage(formErrors, 'source')}>
                      <FormLabel htmlFor="name">
                        <Heading as="h6" size="xs" mt={3}>
                          {t('businessCopy.source')}
                        </Heading>
                      </FormLabel>
                      <Input
                        placeholder={t('businessCopy.enterSource')}
                        _placeholder={{ fontSize: '14px' }}
                        data-testid="sourceField"
                        variant="filled"
                        id="source"
                        type="text"
                        value={initialState.source}
                        onChange={handleChange}
                      />
                      <FormErrorMessage>{getErrorMessage(formErrors, 'source')}</FormErrorMessage>
                    </FormControl>
                  </Box>
                </Flex>

                <Flex>
                  <Box width="50%" mr={6}>
                    <FormControl isInvalid={hasErrorMessage(formErrors, 'campaign')}>
                      <FormLabel htmlFor="name">
                        <Heading as="h6" size="xs" mt={3}>
                          {t('businessCopy.campaign')}
                        </Heading>
                      </FormLabel>
                      <Input
                        placeholder={t('businessCopy.enterCampaign')}
                        _placeholder={{ fontSize: '14px' }}
                        data-testid="campaignField"
                        variant="filled"
                        id="campaign"
                        type="text"
                        value={initialState.campaign}
                        onChange={handleChange}
                      />
                      <FormErrorMessage>{getErrorMessage(formErrors, 'campaign')}</FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box width="50%">
                    <FormControl isInvalid={hasErrorMessage(formErrors, 'medium')}>
                      <FormLabel htmlFor="name">
                        <Heading as="h6" size="xs" mt={3}>
                          {t('businessCopy.medium')}
                        </Heading>
                      </FormLabel>
                      <Input
                        placeholder={t('businessCopy.enterMedium')}
                        _placeholder={{ fontSize: '14px' }}
                        data-testid="mediumField"
                        variant="filled"
                        id="medium"
                        type="text"
                        value={initialState.medium}
                        onChange={handleChange}
                      />
                      <FormErrorMessage>{getErrorMessage(formErrors, 'medium')}</FormErrorMessage>
                    </FormControl>
                  </Box>
                </Flex>
              </>
            )}
            <Divider mt={3} />
          </Flex>
        </ModalBody>
        <ModalFooter justifyContent="end" mb={2}>
          <Box>
            <Button colorScheme="red" data-testid="cancelBtn" mr={3} onClick={handleCancel}>
              {t('businessCopy.cancelBtn')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleBusinessLinkCopy}
              isLoading={isLoading}
              data-testid="copyBusinessLink"
              alignSelf="right"
            >
              {t('businessCopy.createAndCopy')}
            </Button>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BusinessShareModal;
